// Copyright (c) 2026. LodgIQ Inc, All rights reserved
//
// Demand heatmap calendar widget for Ask LodgIQ. Opened by the agent's
// show_demand_calendar tool with per-day OTB/forecast occupancy + events.
// Each day is tinted by forecast occupancy; clicking a day deep-links the
// dashboard to that date (via the same machinery as the source chips).

import React, { useMemo, useRef, useState } from "react";
import { runDemandCalendar } from "../../services/wizard.service";

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MAX_SEGMENTS = 6;        // keeps the payload and the chip row sane
const DURATIONS = [30, 60];    // days; server caps at 62

// Demand mode: occupancy → heat band (0..4). Forecast preferred, OTB as fallback.
const band = (occ) => {
  if (occ == null) return null;
  if (occ >= 90) return 4;
  if (occ >= 75) return 3;
  if (occ >= 60) return 2;
  if (occ >= 40) return 1;
  return 0;
};

// Pace mode: OTB vs same-time-last-year, in points → band p0 (far behind) .. p4 (far ahead).
// SDLY is measured at the same distance from arrival, so the comparison is fair
// even for far-out dates (unlike OTB vs final forecast).
const paceBand = (otb, ly) => {
  if (otb == null || ly == null) return null;
  const gap = otb - ly;
  if (gap <= -10) return 0;
  if (gap < -3) return 1;
  if (gap <= 3) return 2;
  if (gap <= 10) return 3;
  return 4;
};

const iso = (d) => d.toISOString().slice(0, 10);
const day10 = (s) => (s ? String(s).slice(0, 10) : "");

function moneyFmt(currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD", maximumFractionDigits: 0 });
  } catch (_) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
  }
}

export default function DemandCalendarCard({ widget, propertyId, onOpenDate, onInterpret }) {
  const money = moneyFmt(widget && widget.currency);
  const [mode, setMode] = useState("demand");   // "demand" | "pace"

  // AL-5: the card owns its own data once the user filters, so segment/duration
  // changes go to the token-free endpoint instead of back through the model.
  const [view, setView] = useState(widget || {});
  const [picked, setPicked] = useState(((widget || {}).selection || {}).labels || []);
  const [openPicker, setOpenPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [granular, setGranular] = useState(false);
  const spanRef = useRef(null);

  const options = view.segment_options || (widget || {}).segment_options || {};
  const isSegment = view.grain === "segment" && picked.length > 0;

  const track = (event, props) => {
    try { if (window.analyze && window.analyze.track) window.analyze.track(event, props || {}); } catch (_) {}
  };

  const reload = async (nextPicked, days) => {
    if (!propertyId) return;
    setBusy(true);
    setErr("");
    try {
      const from = view.from || widget.from;
      const to = days
        ? (() => { const d = new Date(`${from}T12:00:00`); d.setDate(d.getDate() + days - 1); return iso(d); })()
        : (view.to || widget.to);
      const res = await runDemandCalendar(propertyId, { from, to, segments: nextPicked });
      setView(res);
      setPicked(nextPicked);
      spanRef.current = days || spanRef.current;
      track("askq_calendar_filter", {
        segments: nextPicked.length, days: days || null, property_id: propertyId,
      });
    } catch (e) {
      setErr((e && e.message) || "Couldn't load that selection.");
    } finally {
      setBusy(false);
    }
  };

  const toggleSegment = (name) => {
    const next = picked.includes(name)
      ? picked.filter((p) => p !== name)
      : (picked.length >= MAX_SEGMENTS ? picked : [...picked, name]);
    reload(next);
  };

  const { days, months, eventsByDate } = useMemo(() => {
    const days = (view && view.days) || [];
    const events = (view && view.events) || [];
    const byDate = {};
    days.forEach((r) => { if (r && r.d) byDate[r.d] = r; });

    const evByDate = {};
    events.forEach((e) => {
      const s = day10(e.start), t = day10(e.end) || s;
      if (!s) return;
      const cur = new Date(`${s}T12:00:00`);
      const stop = new Date(`${t}T12:00:00`);
      let guard = 0;
      while (cur <= stop && guard < 120) {
        const k = iso(cur);
        if (byDate[k]) (evByDate[k] = evByDate[k] || []).push(e);
        cur.setDate(cur.getDate() + 1);
        guard += 1;
      }
    });

    // Group into month blocks, padding the first week so weekdays align.
    const blocks = [];
    let block = null;
    days.forEach((r) => {
      const dt = new Date(`${r.d}T12:00:00`);
      const mkey = `${dt.getFullYear()}-${dt.getMonth()}`;
      if (!block || block.key !== mkey) {
        block = {
          key: mkey,
          label: dt.toLocaleDateString(undefined, { month: "long", year: "numeric" }),
          cells: new Array(dt.getDay()).fill(null),   // leading blanks
        };
        blocks.push(block);
      }
      block.cells.push(r);
    });
    return { days, months: blocks, eventsByDate: evByDate };
  }, [view]);

  if (!days.length) {
    return <div className="wiz-calc wiz-calc--fallback">The demand calendar couldn't load any dates for this period.</div>;
  }
  if (widget && widget.schema_version && widget.schema_version !== 1) {
    return <div className="wiz-calc wiz-calc--fallback">This calendar needs a newer app version. Please refresh.</div>;
  }

  const todayKey = iso(new Date());
  const hasPace = days.some((r) => r && r.otb != null && r.ly != null);
  const paceMode = mode === "pace" && hasPace;

  const cellTitle = (r) => {
    const parts = [new Date(`${r.d}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })];
    if (isSegment) {
      // A segment has no capacity of its own — say "of the house" so the number
      // is never mistaken for that segment's occupancy.
      if (r.otb_rooms != null) parts.push(`OTB ${r.otb_rooms} rooms${r.otb != null ? ` (${r.otb}% of house)` : ""}`);
      if (r.fc_rooms != null) parts.push(`Forecast ${r.fc_rooms} rooms${r.fc != null ? ` (${r.fc}% of house)` : ""}`);
    } else {
      if (r.otb != null) parts.push(`OTB ${r.otb}%`);
      if (r.ly != null) {
        parts.push(`Same time LY ${r.ly}%`);
        if (r.otb != null) {
          const gap = r.otb - r.ly;
          parts.push(`Pace ${gap >= 0 ? "+" : ""}${gap.toFixed(1)} pts`);
        }
      }
      if (r.fc != null) parts.push(`Forecast ${r.fc}%`);
    }
    if (r.adr != null) parts.push(`ADR ${money.format(r.adr)}`);
    (eventsByDate[r.d] || []).forEach((e) => parts.push(`Event: ${e.title}`));
    parts.push("Click to open on the dashboard");
    return parts.join(" · ");
  };

  const scope = isSegment ? ` for ${picked.join(" + ")}` : "";
  const interpret = () => {
    if (!onInterpret) return;
    const from = view.from || widget.from;
    const to = view.to || widget.to;
    onInterpret(
      paceMode
        ? `Looking at pace${scope} for ${from} to ${to}: which dates are furthest behind ` +
          "same-time-last-year, what might explain it, and what actions would you consider?"
        : isSegment
          ? `Looking at ${picked.join(" + ")} demand for ${from} to ${to} (rooms and share of the ` +
            "house): which dates is this segment carrying, where is it thin, and what would you do?"
          : `Looking at ${from} to ${to}: which dates need my attention — ` +
            "where are we pacing behind, and where is demand strong enough to test higher rates?"
    );
  };

  return (
    <div className="wiz-calc wiz-cal">
      <div className="wiz-calc__head">
        <span className="wiz-calc__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="5" width="16" height="16" rx="2.5" />
            <path d="M8 3v4M16 3v4M4 10h16" />
          </svg>
        </span>
        <span className="wiz-calc__title">Demand calendar</span>
        {/* Pace tab stays VISIBLE when last-year data is missing — disabled with an
            explanation — so users know the mode exists (silent hiding read as "pace
            was removed"). */}
        <div className="wiz-sim__seg wiz-cal__mode" role="radiogroup" aria-label="Calendar coloring">
          <button className={!paceMode ? "is-on" : ""} onClick={() => setMode("demand")}
            title="Color by forecast occupancy">Demand</button>
          <button className={paceMode ? "is-on" : ""} disabled={!hasPace}
            onClick={() => hasPace && setMode("pace")}
            style={!hasPace ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
            title={hasPace ? "Color by OTB vs same time last year"
                           : "Pace needs same-time-last-year data — not available for these dates"}>
            Pace</button>
        </div>
        <span className="wiz-sim__date">{view.from || widget.from} → {view.to || widget.to}</span>
      </div>

      {propertyId && (options.top_segments || options.groups) && (
        <div className="wiz-cal__filter">
          <button
            className={`wiz-cal__segbtn ${isSegment ? "is-on" : ""}`}
            onClick={() => setOpenPicker((v) => !v)}
            disabled={busy}
            aria-expanded={openPicker}
            title="Filter the calendar to one or more segments"
          >
            {isSegment ? picked.join(" + ") : "Whole hotel"} <span aria-hidden="true">▾</span>
          </button>
          {busy && <span className="wiz-cal__busy">loading…</span>}
          <span className="wiz-cal__span">
            {DURATIONS.map((n) => (
              <button
                key={n}
                className={(spanRef.current || 30) === n ? "is-on" : ""}
                onClick={() => reload(picked, n)}
                disabled={busy}
              >{n}d</button>
            ))}
          </span>

          {openPicker && (
            <div className="wiz-cal__picker" role="dialog" aria-label="Choose segments">
              <div className="wiz-cal__picker-head">
                <span>Segments {picked.length ? `(${picked.length}/${MAX_SEGMENTS})` : ""}</span>
                <label className="wiz-cal__gran">
                  <input type="checkbox" checked={granular} onChange={(e) => setGranular(e.target.checked)} />
                  market codes
                </label>
              </div>
              <div className="wiz-cal__picker-list">
                {picked.length > 0 && (
                  <button className="wiz-cal__opt wiz-cal__opt--clear"
                    onClick={() => { reload([]); setOpenPicker(false); }}>
                    ← Back to whole hotel
                  </button>
                )}
                {!granular && (options.top_segments || []).map((o) => (
                  <button key={`t${o.name}`} className={`wiz-cal__opt ${picked.includes(o.name) ? "is-sel" : ""}`}
                    onClick={() => toggleSegment(o.name)}>
                    <span className="wiz-cal__opt-name">{o.name}</span>
                    <span className="wiz-cal__opt-meta">top segment</span>
                  </button>
                ))}
                {!granular && (options.groups || []).map((o) => (
                  <button key={`g${o.name}`} className={`wiz-cal__opt ${picked.includes(o.name) ? "is-sel" : ""}`}
                    onClick={() => toggleSegment(o.name)}>
                    <span className="wiz-cal__opt-name">{o.name}</span>
                    <span className="wiz-cal__opt-meta">{(o.codes || []).length} codes</span>
                  </button>
                ))}
                {granular && (options.codes || []).map((o) => (
                  <button key={`c${o.code}`} className={`wiz-cal__opt ${picked.includes(o.code) ? "is-sel" : ""}`}
                    onClick={() => toggleSegment(o.code)}>
                    <span className="wiz-cal__opt-name">{o.code}</span>
                    <span className="wiz-cal__opt-meta">{o.name || ""}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {err && <div className="wiz-sim__pferr">{err}</div>}
      {view.unknown_segments && view.unknown_segments.length > 0 && (
        <div className="wiz-sim__pferr">Not found on this property: {view.unknown_segments.join(", ")}</div>
      )}
      {isSegment && view.forecast_reason && (
        <div className="wiz-cal__notice">{view.forecast_reason}</div>
      )}

      {months.map((mo) => (
        <div className="wiz-cal__month" key={mo.key}>
          <div className="wiz-cal__month-name">{mo.label}</div>
          <div className="wiz-cal__grid">
            {WEEKDAYS.map((w, i) => (
              <span className="wiz-cal__wd" key={`wd${i}`}>{w}</span>
            ))}
            {mo.cells.map((r, i) => {
              if (!r) return <span className="wiz-cal__blank" key={`b${i}`} />;
              const b = paceMode ? paceBand(r.otb, r.ly) : band(r.fc != null ? r.fc : r.otb);
              const cls = b == null ? "wiz-cal__day--nodata" : paceMode ? `wiz-cal__day--p${b}` : `wiz-cal__day--b${b}`;
              const evs = eventsByDate[r.d];
              const gap = paceMode && r.otb != null && r.ly != null ? r.otb - r.ly : null;
              return (
                <button
                  key={r.d}
                  className={`wiz-cal__day ${cls} ${r.d === todayKey ? "is-today" : ""}`}
                  title={cellTitle(r)}
                  onClick={() => onOpenDate && onOpenDate(r.d)}
                >
                  <span className="wiz-cal__num">{Number(r.d.slice(8, 10))}</span>
                  <span className="wiz-cal__occ">
                    {paceMode
                      ? (gap != null ? `${gap >= 0 ? "+" : ""}${Math.round(gap)}` : "–")
                      : isSegment
                        ? (r.otb_rooms != null ? `${r.otb_rooms}` : "–")
                        : (r.otb != null ? `${Math.round(r.otb)}%` : "–")}
                  </span>
                  {evs && <span className="wiz-cal__ev" aria-label={`${evs.length} event(s)`} />}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className="wiz-cal__legend">
        <span className="wiz-cal__legend-label">
          {paceMode
            ? "OTB vs same time LY"
            : isSegment
              ? `${picked.join(" + ")} — forecast share of house`
              : "Forecast occupancy"}
        </span>
        {(paceMode
          ? [["p0", "≤−10 pts"], ["p1", "−10…−3"], ["p2", "±3"], ["p3", "+3…+10"], ["p4", "+10 pts"]]
          : [["b0", "<40%"], ["b1", "40–60"], ["b2", "60–75"], ["b3", "75–90"], ["b4", "90%+"]]
        ).map(([b, t]) => (
          <span className="wiz-cal__legend-item" key={b}>
            <i className={`wiz-cal__swatch wiz-cal__swatch--${b}`} />{t}
          </span>
        ))}
        <span className="wiz-cal__legend-item"><i className="wiz-cal__swatch wiz-cal__swatch--ev" />event</span>
        <span className="wiz-cal__legend-note">
          {paceMode
            ? "number = pace gap in pts · click a day to open it"
            : isSegment
              ? "number = rooms on the books for this selection · % is share of the whole house, not segment occupancy · click a day to open it"
              : "number = OTB today · click a day to open it"}
        </span>
      </div>

      {onInterpret && (
        <button className="wiz-calc__interpret" onClick={interpret}>Ask LodgIQ to interpret ↗</button>
      )}

      <div className="wiz-calc__foot">© {new Date().getFullYear()} LodgIQ</div>
    </div>
  );
}
